import { ChartLegend, ChartTooltip } from "@mantine/charts";
import {
  Box,
  MantineColor,
  MantineStyleProps,
  getThemeColor,
  useMantineTheme,
} from "@mantine/core";
import { useState } from "react";
import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ScatterCustomizedShape } from "recharts/types/cartesian/Scatter";

import classes from "./ScatterAndLineChart.module.css";
import { CalculateTrendsOptions, calculateTrends } from "./utils";

import type { FC } from "react";
import type { Props as LegendProps } from "recharts/types/component/Legend";
import type { TooltipProps } from "recharts/types/component/Tooltip";
import type { CurveType } from "recharts/types/shape/Curve";

export type ScatterAndLineData = {
  [xKey: string]: any;
};

export type DataKey = {
  x: string;
  y: string;
};

export type TrendlineType = "linear-trend" | "moving-average";

export type ScatterAndLineChartProps = {
  data?: ScatterAndLineData[];
  dataKey: DataKey;
  gridAxis?: "x" | "y" | "xy" | "none";
  h?: number;
  legendProps?: Omit<LegendProps, "ref">;
  lineLabel?: string;
  lineProps?: {
    color?: MantineColor;
    strokeWidth?: number;
    type?: CurveType;
  };
  onMouseLeave?: (event: React.MouseEvent<HTMLDivElement>) => void;
  scatterProps?: {
    color?: MantineColor;
    shape?: ScatterCustomizedShape;
  };
  strokeDasharray?: string;
  tickLine?: "x" | "y" | "xy" | "none";
  tooltipAnimationDuration?: number;
  tooltipProps?: Omit<TooltipProps<any, any>, "ref">;
  trendline?: TrendlineType;
  trendlineOptions?: CalculateTrendsOptions;
  /** When false, the trend line is not rendered. Defaults to true. */
  withTrendline?: boolean;
  unit?: Partial<DataKey>;
  valueFormatter?: {
    x?: (value: any) => string;
    y?: (value: any) => string;
  };
  xAxisProps?: {
    domain?: [number, number];
  };
  withLegend?: boolean;
  withTooltip?: boolean;
  withXAxis?: boolean;
  withYAxis?: boolean;
} & Omit<MantineStyleProps, "className" | "onMouseLeave">;

export const ScatterAndLineChart: FC<ScatterAndLineChartProps> = ({
  data = [],
  dataKey,
  gridAxis = "x",
  h = 300,
  legendProps,
  lineLabel,
  lineProps,
  onMouseLeave,
  scatterProps,
  strokeDasharray = "5 5",
  tickLine = "y",
  tooltipAnimationDuration = 0,
  tooltipProps,
  trendline = "linear-trend",
  trendlineOptions,
  withTrendline = true,
  unit,
  valueFormatter,
  xAxisProps,
  withLegend = true,
  withTooltip = true,
  withXAxis = true,
  withYAxis = true,
  ...props
}) => {
  const theme = useMantineTheme();

  const items = withTrendline ? calculateTrends(data, dataKey, trendline, trendlineOptions) : data;
  const withXTickLine = gridAxis !== "none" && (tickLine === "x" || tickLine === "xy");
  const withYTickLine = gridAxis !== "none" && (tickLine === "y" || tickLine === "xy");
  const [_highlightedArea, setHighlightedArea] = useState<string | null>(null);

  const handleMouseLeave = (event: React.MouseEvent<HTMLDivElement>) => {
    setHighlightedArea(null);
    onMouseLeave?.(event);
  };

  return (
    <Box className={classes.root} onMouseLeave={handleMouseLeave} {...props}>
      <ResponsiveContainer className={classes.container} width="100%" height={h}>
        <ComposedChart data={items}>
          <CartesianGrid
            className={classes.grid}
            strokeDasharray={strokeDasharray}
            vertical={gridAxis === "y" || gridAxis === "xy"}
            horizontal={gridAxis === "x" || gridAxis === "xy"}
          />
          <XAxis
            hide={!withXAxis}
            className={classes.axis}
            type="number"
            dataKey={dataKey.x}
            tick={{ transform: "translate(0, 10)", fontSize: 12, fill: "currentColor" }}
            stroke=""
            interval="preserveStartEnd"
            tickLine={withXTickLine ? { stroke: "currentColor" } : false}
            minTickGap={5}
            unit={unit?.x}
            tickFormatter={valueFormatter?.x}
            {...xAxisProps}
          />
          <YAxis
            className={classes.axis}
            axisLine={false}
            type="number"
            dataKey={dataKey.y}
            tick={{ transform: "translate(-10, 0)", fontSize: 12, fill: "currentColor" }}
            hide={!withYAxis}
            tickLine={withYTickLine ? { stroke: "currentColor" } : false}
            allowDecimals
            stroke=""
            unit={unit?.y}
            tickFormatter={valueFormatter?.y}
          />

          {withLegend && withTrendline && (
            <Legend
              verticalAlign="top"
              content={({ payload }) => (
                <ChartLegend
                  payload={payload
                    ?.filter((item) => item.dataKey === `${trendline}_${dataKey.y}`)
                    .map((item) => ({
                      ...item,
                      dataKey: lineLabel || item.dataKey,
                    }))}
                  onHighlight={(area) => setHighlightedArea(typeof area === "string" ? area : null)}
                  legendPosition={legendProps?.verticalAlign || "top"}
                />
              )}
              {...legendProps}
            />
          )}

          {withTooltip && (
            <Tooltip
              animationDuration={tooltipAnimationDuration}
              isAnimationActive={tooltipAnimationDuration !== 0}
              position={{ y: 0 }}
              content={({ label, payload }) => (
                <ChartTooltip label={label} payload={payload ? [...payload] : undefined} />
              )}
              {...tooltipProps}
            />
          )}

          {withTrendline && (
            <Line
              isAnimationActive={false}
              connectNulls
              dataKey={`${trendline}_${dataKey.y}`}
              stroke={getThemeColor(lineProps?.color, theme)}
              dot={false}
              activeDot={false}
              strokeWidth={lineProps?.strokeWidth || 2}
              type={lineProps?.type}
            />
          )}
          <Scatter
            isAnimationActive={false}
            dataKey={dataKey.y}
            fill={getThemeColor(scatterProps?.color, theme)}
            shape={scatterProps?.shape}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
};
